import { useState, useMemo, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import {
  Box,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Checkbox,
  Avatar,
  Chip,
  Badge,
  Tabs,
  Tab,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  Rating,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Tooltip,
  InputAdornment,
  alpha,
  useTheme,
} from '@mui/material'
import {
  Store as ShipperIcon,
  LocalShipping as FreighterIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  PersonAdd as PersonAddIcon,
  VerifiedUser as VerifiedUserIcon,
  CalendarMonth as CalendarIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon,
  Send as SendIcon,
  PowerSettingsNew as PowerIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import {
  accountService,
  userService,
  driverService,
  vehicleService,
  kycService,
  scoreService,
  subscriptionService,
  subscriptionPlanService,
  financeService,
  shipmentService,
  type SubscriptionPlanDto,
  type SubscriptionDto,
} from '../api/adminServices'
import { formatCurrency } from '../utils/formatCurrency'
import { shipperColor, freighterColor } from '../theme/theme'
// Admin color
const adminColor = '#f59e0b' // Amber/Orange for Admin
import { formatAttributeName } from '../utils/formatAttributeName'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import VerificationReviewDialog from '../components/VerificationReviewDialog'
import UserDetailDialog from '../components/UserDetailDialog'
import PageRefreshButton from '../components/PageRefreshButton'
import InviteUserDialog from '../components/InviteUserDialog'
import ConfirmDialog from '../components/ConfirmDialog'
import { useToast } from '../components/useToast'
import { ErrorCard, TableSkeleton } from '../components/Feedback'
import { EntityProperties } from '../components/EntityProperties'
import AccountOverview from '../components/AccountOverview'
import AccountSubscriptionActions, {
  AccountSubscriptionMenuEntry,
} from '../components/AccountSubscriptionActions'
import { accountSchema, vehicleSchema, driverSchema, userSchema } from '../components/entitySchemas'
import VehicleUnavailabilityCalendar from '../components/VehicleUnavailabilityCalendar'
import SubjectDocuments from '../components/SubjectDocuments'
import { fmtAge, fmtDateTime } from '../utils/propertyFormat'

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode
  tabKey: string
  value: string
}

function TabPanel({ children, value, tabKey, ...other }: TabPanelProps) {
  const isActive = value === tabKey
  return (
    <Box
      role="tabpanel"
      hidden={!isActive}
      id={`tabpanel-${tabKey}`}
      aria-labelledby={`tab-${tabKey}`}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: isActive ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: 'hidden', // Remove overflow from TabPanel - let TableContainer handle it
      }}
      {...other}
    >
      {isActive && <Box sx={{ p: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>}
    </Box>
  )
}

export default function UnifiedAccountsPage() {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [searchParams] = useSearchParams()
  const getLocalizedLabel = useLocalizedLabel()

  // ==================== STATE ====================
  // Filters - initialize from URL params
  const typeFromUrl = searchParams.get('type')?.toUpperCase() as 'ALL' | 'SHIPPER' | 'FREIGHTER' | null
  const [accountTypeFilter, setAccountTypeFilter] = useState<'ALL' | 'SHIPPER' | 'FREIGHTER'>(
    typeFromUrl && ['SHIPPER', 'FREIGHTER'].includes(typeFromUrl) ? typeFromUrl : 'ALL'
  )
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL')
  const [searchQuery, setSearchQuery] = useState('')
  const [pendingKycFilter, setPendingKycFilter] = useState(false)
  /** Only accounts created within the last N days; 0 = any time. */
  const [createdWithinDays, setCreatedWithinDays] = useState(0)

  // Selection
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)
  const [rightTab, setRightTab] = useState<string>('overview')

  // Resizable splitter
  const [leftPanelWidth, setLeftPanelWidth] = useState(45)
  const [isResizing, setIsResizing] = useState(false)

  // Edit mode
  const [editMode, setEditMode] = useState(false)
  const [editedAccount, setEditedAccount] = useState<any>(null)

  // Action menu
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null)
  const [actionMenuAccountId, setActionMenuAccountId] = useState<string | null>(null)
  const [subscriptionMenuAnchor, setSubscriptionMenuAnchor] = useState<null | HTMLElement>(null)

  // Entity action menus
  const [vehicleMenuAnchor, setVehicleMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null)
  const [driverMenuAnchor, setDriverMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectedDriver, setSelectedDriver] = useState<any>(null)
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectedUser, setSelectedUser] = useState<any>(null)

  // Dialogs
  const [inviteUserDialogOpen, setInviteUserDialogOpen] = useState(false)
  const [userDetailOpen, setUserDetailOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<
    { kind: 'vehicle' | 'driver' | 'user'; id: string; label: string } | null
  >(null)
  const [userDetailId, setUserDetailId] = useState<string | undefined>(undefined)

  const [entityDialogOpen, setEntityDialogOpen] = useState(false)
  const [entityDialogType, setEntityDialogType] = useState<'vehicle' | 'driver' | 'user'>('user')
  const [entityDialogData, setEntityDialogData] = useState<any>(null)
  const [entityDialogEditMode, setEntityDialogEditMode] = useState(false)
  const [entityDialogTab, setEntityDialogTab] = useState(0)

  const [scoreDialogOpen, setScoreDialogOpen] = useState(false)
  const [scoreDialogEntity, setScoreDialogEntity] = useState<{ type: string; id: string; name: string } | null>(null)
  const [scoreAdjustment, setScoreAdjustment] = useState(0)
  const [scoreReason, setScoreReason] = useState('')

  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false)
  const [calendarEntity, setCalendarEntity] = useState<{ type: string; id: string; name: string } | null>(null)

  const [kycDialogOpen, setKycDialogOpen] = useState(false)
  const [kycReviewVerificationId, setKycReviewVerificationId] = useState<string | undefined>(undefined)
  const [kycInitiateParams, setKycInitiateParams] = useState<
    { subjectType: 'DRIVER' | 'VEHICLE'; subjectId: string; accountId: string } | undefined
  >(undefined)

  const [documentDialogOpen, setDocumentDialogOpen] = useState(false)
  const [documentEntity, setDocumentEntity] = useState<{ type: string; id: string; name: string; data: any } | null>(null)

  // ==================== DATA FETCHING ====================
  // Fetch all accounts
  const {
    data: accountsData,
    isLoading: accountsLoading,
    isFetching: accountsFetching,
    isError: accountsError,
    error: accountsErrObj,
    refetch: refetchAccounts,
  } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountService.getAll(0, 1000),
  })

  // Fetch selected account details
  const { data: selectedAccount } = useQuery({
    queryKey: ['account', selectedAccountId],
    queryFn: () => selectedAccountId ? accountService.getById(selectedAccountId) : null,
    enabled: !!selectedAccountId,
  })

  // Fetch users for selected account
  const { data: usersData } = useQuery({
    queryKey: ['users', selectedAccountId],
    queryFn: () => selectedAccountId ? accountService.getUsersByAccount(selectedAccountId, 0, 100) : null,
    enabled: !!selectedAccountId,
  })

  // Fetch drivers for selected account (Freighter only)
  const { data: driversData } = useQuery({
    queryKey: ['drivers', selectedAccountId],
    queryFn: () => selectedAccountId ? driverService.getAllByAccount(selectedAccountId) : null,
    enabled: !!selectedAccountId && getAccountType(selectedAccountId) === 'FREIGHTER',
  })

  // Fetch vehicles for selected account (Freighter only)
  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles', selectedAccountId],
    queryFn: () => selectedAccountId ? vehicleService.getAllByAccount(selectedAccountId) : null,
    enabled: !!selectedAccountId && getAccountType(selectedAccountId) === 'FREIGHTER',
  })

  // Fetch KYC pending verifications
  const { data: kycPendingData } = useQuery({
    queryKey: ['kyc-pending'],
    queryFn: () => kycService.getPending(0, 100),
  })

  // Subscriptions for overview tab and account action menus.
  const { data: subscriptionsPage } = useQuery({
    queryKey: ['subscriptions-for-accounts'],
    queryFn: () => subscriptionService.getAll(0, 500),
  })

  const { data: subscriptionPlansPage } = useQuery({
    queryKey: ['subscription-plans-active'],
    queryFn: () => subscriptionPlanService.getAll(false, 0, 200),
  })

  const subscriptionPlans: SubscriptionPlanDto[] = useMemo(
    () => subscriptionPlansPage?.content ?? subscriptionPlansPage ?? [],
    [subscriptionPlansPage],
  )

  const planById = useMemo(() => {
    const map = new Map<string, SubscriptionPlanDto>()
    subscriptionPlans.forEach((p) => map.set(p.id, p))
    return map
  }, [subscriptionPlans])

  const allSubscriptions: SubscriptionDto[] = useMemo(
    () => subscriptionsPage?.content ?? subscriptionsPage ?? [],
    [subscriptionsPage],
  )

  const getSubscriptionsForAccount = useCallback(
    (accountId: string) => allSubscriptions.filter((s) => s.accountId === accountId),
    [allSubscriptions],
  )

  const selectedAccountSubscriptions = useMemo(
    () => (selectedAccountId ? getSubscriptionsForAccount(selectedAccountId) : []),
    [selectedAccountId, getSubscriptionsForAccount],
  )

  // Fetch account-scoped payments / payouts when the Finance tab is opened
  const { data: accountPaymentsPage } = useQuery({
    queryKey: ['account-payments', selectedAccountId],
    queryFn: () =>
      financeService.getAllPayments({
        accountId: selectedAccountId as string,
        size: 20,
      }),
    enabled: !!selectedAccountId && rightTab === 'finance',
  })
  const { data: accountPayoutsPage } = useQuery({
    queryKey: ['account-payouts', selectedAccountId],
    queryFn: () =>
      financeService.getAllPayouts({
        accountId: selectedAccountId as string,
        size: 20,
      }),
    enabled: !!selectedAccountId && rightTab === 'finance',
  })

  // ==================== MUTATIONS ====================
  const activateMutation = useMutation({
    mutationFn: (accountId: string) => accountService.activate(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast.success('Account activated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to activate account'),
  })

  const deactivateMutation = useMutation({
    mutationFn: (accountId: string) => accountService.deactivate(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast.success('Account deactivated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to deactivate account'),
  })

  const updateAccountMutation = useMutation({
    mutationFn: ({ accountId, account }: { accountId: string; account: any }) =>
      accountService.update(accountId, account),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['account', selectedAccountId] })
      setEditMode(false)
      toast.success('Account updated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to update account'),
  })

  const entitySaveMutation = useMutation({
    mutationFn: async () => {
      if (!entityDialogData?.id) throw new Error('Missing entity ID')
      const id = entityDialogData.id as string
      switch (entityDialogType) {
        case 'vehicle':
          return vehicleService.update(id, entityDialogData)
        case 'driver': {
          const { user, ...rest } = entityDialogData
          await driverService.update(id, rest)
          if (user && typeof user === 'object' && user.id) {
            await userService.update(user.id, user)
          }
          return null
        }
        case 'user':
          return userService.update(id, entityDialogData)
        default:
          throw new Error(`Unsupported entity type: ${entityDialogType}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', selectedAccountId] })
      queryClient.invalidateQueries({ queryKey: ['drivers', selectedAccountId] })
      queryClient.invalidateQueries({ queryKey: ['users', selectedAccountId] })
      toast.success(`${entityDialogType.charAt(0).toUpperCase() + entityDialogType.slice(1)} updated`)
      setEntityDialogEditMode(false)
      setEntityDialogOpen(false)
      setEntityDialogTab(0)
    },
    onError: (e) => toast.errorFrom(e, `Failed to update ${entityDialogType}`),
  })

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => userService.delete(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', selectedAccountId] })
      toast.success('User deleted')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to delete user'),
  })

  const activateUserMutation = useMutation({
    mutationFn: (userId: string) => userService.activate(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', selectedAccountId] })
      toast.success('User activated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to activate user'),
  })

  const deactivateUserMutation = useMutation({
    mutationFn: (userId: string) => userService.deactivate(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', selectedAccountId] })
      toast.success('User deactivated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to deactivate user'),
  })

  const deleteVehicleMutation = useMutation({
    mutationFn: (vehicleId: string) => vehicleService.delete(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', selectedAccountId] })
      toast.success('Vehicle deleted')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to delete vehicle'),
  })

  const activateVehicleMutation = useMutation({
    mutationFn: (vehicleId: string) => vehicleService.activate(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', selectedAccountId] })
      toast.success('Vehicle activated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to activate vehicle'),
  })

  const deactivateVehicleMutation = useMutation({
    mutationFn: (vehicleId: string) => vehicleService.deactivate(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', selectedAccountId] })
      toast.success('Vehicle deactivated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to deactivate vehicle'),
  })

  const deleteDriverMutation = useMutation({
    mutationFn: (driverId: string) => driverService.delete(driverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers', selectedAccountId] })
      toast.success('Driver deleted')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to delete driver'),
  })

  const adjustScoreMutation = useMutation({
    mutationFn: ({ entityType, entityId, request }: { entityType: string; entityId: string; request: any }) =>
      scoreService.adjustScore(entityType, entityId, request),
    onSuccess: () => {
      setScoreDialogOpen(false)
      setScoreAdjustment(0)
      setScoreReason('')
      toast.success('Score adjusted')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to adjust score'),
  })

  // ==================== HELPERS ====================
  // Get account type helper - API uses 'type' field
  function getAccountType(accountId: string): string {
    const accounts = accountsData?.content || accountsData || []
    if (!Array.isArray(accounts)) return ''
    const account = accounts.find((a: any) => a.id === accountId)
    return String(account?.type || '').toUpperCase()
  }

  // Get accounts array from data
  const getAccountsArray = useCallback(() => {
    if (!accountsData) return []
    if (Array.isArray(accountsData)) return accountsData
    if (accountsData.content && Array.isArray(accountsData.content)) return accountsData.content
    return []
  }, [accountsData])

  // Filter accounts
  const filteredAccounts = useMemo(() => {
    const accounts = getAccountsArray()
    if (accounts.length === 0) return []

    return accounts.filter((acc: any) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const searchableFields = [
          acc.businessName,
          acc.contactEmail,
          acc.contactPhone,
          acc.id,
          acc.organizationCode,
        ]
        const matches = searchableFields.some(
          (field) => field && String(field).toLowerCase().includes(query)
        )
        if (!matches) return false
      }

      // Filter by account type - use 'type' field from API
      if (accountTypeFilter !== 'ALL') {
        const accType = String(acc.type || '').toUpperCase()
        if (accType !== accountTypeFilter) return false
      }

      // Filter by status - API uses the 'status' lifecycle enum (ACTIVE | INACTIVE | PENDING_PROFILE)
      if (statusFilter !== 'ALL') {
        const isActive = String(acc.status || '').toUpperCase() === 'ACTIVE'
        if (statusFilter === 'ACTIVE' && !isActive) return false
        if (statusFilter === 'INACTIVE' && isActive) return false
      }

      // Filter by pending KYC
      if (pendingKycFilter) {
        const kycData = kycPendingData?.content || kycPendingData || []
        if (!Array.isArray(kycData)) return false
        const pendingAccountIds = new Set(kycData.map((v: any) => v.accountId))
        if (!pendingAccountIds.has(acc.id)) return false
      }

      // Filter by creation date (newly created accounts)
      if (createdWithinDays > 0) {
        const created = acc.createdAt ? new Date(acc.createdAt).getTime() : NaN
        if (isNaN(created) || created < Date.now() - createdWithinDays * 86400000) return false
      }

      return true
    })
  }, [getAccountsArray, accountTypeFilter, statusFilter, pendingKycFilter, kycPendingData, searchQuery, createdWithinDays])

  const actionMenuAccount = useMemo(
    () => filteredAccounts.find((a: any) => a.id === actionMenuAccountId) ?? null,
    [filteredAccounts, actionMenuAccountId],
  )

  const actionMenuAccountType = String(actionMenuAccount?.type || '').toUpperCase()
  const actionMenuSubscriptions = actionMenuAccountId
    ? getSubscriptionsForAccount(actionMenuAccountId)
    : []

  // Get user count for account
  const getUserCount = useCallback((accountId: string): number => {
    // First try to get from account data
    const accounts = getAccountsArray()
    const account = accounts.find((a: any) => a.id === accountId)
    if (account?.userCount !== undefined) return account.userCount
    if (account?.usersCount !== undefined) return account.usersCount
    
    // If account is selected, use usersData
    if (accountId === selectedAccountId && usersData) {
      const users = usersData?.content || usersData || []
      return Array.isArray(users) ? users.length : 0
    }
    
    return 0
  }, [selectedAccountId, usersData, getAccountsArray])

  // Get account score
  const getAccountScore = useCallback((accountId: string): number => {
    const accounts = getAccountsArray()
    const account = accounts.find((a: any) => a.id === accountId)
    return account?.score || account?.accountScore || 500
  }, [getAccountsArray])

  // ==================== RESIZABLE SPLITTER ====================
  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault()
      const container = document.getElementById('unified-accounts-container')
      if (!container) return
      const rect = container.getBoundingClientRect()
      const newWidth = ((e.clientX - rect.left) / rect.width) * 100
      setLeftPanelWidth(Math.max(20, Math.min(70, newWidth)))
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    // Prevent text selection while dragging
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  // ==================== HANDLERS ====================
  const handleAccountSelect = (accountId: string) => {
    setSelectedAccountId(accountId)
    setRightTab('overview')
    setEditMode(false)
    const accounts = getAccountsArray()
    const account = accounts.find((a: any) => a.id === accountId)
    setEditedAccount(account ? { ...account } : null)
  }

  const handleSaveAccount = () => {
    if (selectedAccountId && editedAccount) {
      updateAccountMutation.mutate({ accountId: selectedAccountId, account: editedAccount })
    }
  }

  const openUserDetail = (userId: string) => {
    setUserDetailId(userId)
    setUserDetailOpen(true)
  }

  const openEntityDialog = (type: 'vehicle' | 'driver' | 'user', data: any, editMode = false) => {
    setEntityDialogType(type)
    setEntityDialogData(data)
    setEntityDialogEditMode(editMode)
    setEntityDialogOpen(true)
  }

  const openScoreDialog = (type: string, id: string, name: string) => {
    setScoreDialogEntity({ type, id, name })
    setScoreAdjustment(0)
    setScoreReason('')
    setScoreDialogOpen(true)
  }

  const openCalendarDialog = (type: string, id: string, name: string) => {
    setCalendarEntity({ type, id, name })
    setCalendarDialogOpen(true)
  }

  const openKycDialog = (
    type: 'driver' | 'vehicle',
    id: string,
    _name: string,
    action: 'initiate' | 'review',
    accountId: string
  ) => {
    const subjectType = type === 'driver' ? 'DRIVER' : 'VEHICLE'
    if (action === 'review') {
      // Find an existing verification for this subject from the cached pending list.
      const list = (kycPendingData?.content || kycPendingData || []) as any[]
      const existing = Array.isArray(list)
        ? list.find((v) => v?.subjectId === id && v?.subjectType === subjectType)
        : null
      if (existing?.id) {
        setKycReviewVerificationId(existing.id)
        setKycInitiateParams(undefined)
      } else {
        // No pending verification — fall back to initiate mode.
        setKycReviewVerificationId(undefined)
        setKycInitiateParams({ subjectType, subjectId: id, accountId })
      }
    } else {
      setKycReviewVerificationId(undefined)
      setKycInitiateParams({ subjectType, subjectId: id, accountId })
    }
    setKycDialogOpen(true)
  }

  const openDocumentDialog = (type: string, id: string, name: string, data: any) => {
    setDocumentEntity({ type, id, name, data })
    setDocumentDialogOpen(true)
  }

  // ==================== RENDER HELPERS ====================
  const renderKycChip = (status?: string) => (
    <Chip
      label={getLocalizedLabel('kycVerificationStatus', status || 'PENDING')}
      size="small"
      color={status === 'VERIFIED' ? 'success' : status === 'REJECTED' || status === 'EXPIRED' ? 'error' : 'warning'}
    />
  )

  const renderAccountIcon = (accountType: string) => {
    const type = String(accountType || '').toUpperCase()
    if (type === 'SHIPPER') {
      return (
        <Tooltip title="Shipper">
          <Avatar sx={{ bgcolor: alpha(shipperColor, 0.1), width: 36, height: 36 }}>
            <ShipperIcon fontSize="small" sx={{ color: shipperColor }} />
          </Avatar>
        </Tooltip>
      )
    }
    if (type === 'ADMIN') {
      return (
        <Tooltip title="Admin">
          <Avatar sx={{ bgcolor: alpha(adminColor, 0.1), width: 36, height: 36 }}>
            <AdminIcon fontSize="small" sx={{ color: adminColor }} />
          </Avatar>
        </Tooltip>
      )
    }
    return (
      <Tooltip title="Freighter">
        <Avatar sx={{ bgcolor: alpha(freighterColor, 0.1), width: 36, height: 36 }}>
          <FreighterIcon fontSize="small" sx={{ color: freighterColor }} />
        </Avatar>
      </Tooltip>
    )
  }

  // Helper function to flatten driver object (extract nested "user" object properties)
  const flattenDriver = useCallback((driver: any) => {
    if (!driver) return driver
    const flattened = { ...driver }
    if (driver.user && typeof driver.user === 'object') {
      // Extract user properties and prefix with "user."
      Object.keys(driver.user).forEach((userKey) => {
        flattened[`user.${userKey}`] = driver.user[userKey]
      })
      // Remove the nested user object
      delete flattened.user
    }
    return flattened
  }, [])

  const renderCellValue = (key: string, value: any) => {
    // Handle null, undefined, empty string, and "null" string
    if (value === null || value === undefined || value === '' || value === 'null') return '-'
    
    // Handle the account lifecycle 'status' enum (ACTIVE | INACTIVE | PENDING_PROFILE)
    if (key === 'status') {
      const status = String(value).toUpperCase()
      return (
        <Chip
          label={getLocalizedLabel('accountStatus', status) || status}
          size="small"
          color={status === 'ACTIVE' ? 'success' : 'default'}
        />
      )
    }
    
    // Handle status field - try to determine which enum to use based on context
    if (key.toLowerCase() === 'status') {
      const statusValue = String(value)
      if (statusValue === 'null' || !statusValue) return '-'
      
      // Try userStatus first (most common)
      let localizedStatus = getLocalizedLabel('userStatus', statusValue)
      // If not found, try other status enums
      if (localizedStatus === statusValue) {
        localizedStatus = getLocalizedLabel('kycVerificationStatus', statusValue)
      }
      if (localizedStatus === statusValue) {
        localizedStatus = getLocalizedLabel('paymentStatus', statusValue)
      }
      if (localizedStatus === statusValue) {
        localizedStatus = getLocalizedLabel('shipmentStatus', statusValue)
      }
      if (localizedStatus === statusValue) {
        localizedStatus = getLocalizedLabel('jobStatus', statusValue)
      }
      
      return (
        <Chip
          label={localizedStatus}
          size="small"
          color={statusValue.toUpperCase() === 'ACTIVE' || statusValue.toUpperCase() === 'VERIFIED' ? 'success' : 'default'}
        />
      )
    }
    
    // Helper function to get enum name from field key
    const getEnumNameFromKey = (fieldKey: string): string | null => {
      const lowerKey = fieldKey.toLowerCase()
      const enumFieldMap: Record<string, string> = {
        type: 'accountType', // Default for 'type' field
        accounttype: 'accountType',
        account_type: 'accountType',
        vehicletype: 'vehicleType',
        vehicle_type: 'vehicleType',
        loadingside: 'loadingSide',
        loading_side: 'loadingSide',
        paymentmethod: 'paymentMethod',
        payment_method: 'paymentMethod',
        paymentterms: 'paymentTerms',
        payment_terms: 'paymentTerms',
        defaultcurrency: 'currency',
        currency: 'currency',
        cargotype: 'cargoType',
        cargo_type: 'cargoType',
        priority: 'priority',
        shipmenturgency: 'shipmentUrgency',
        shipment_urgency: 'shipmentUrgency',
        billingcycle: 'billingCycle',
        billing_cycle: 'billingCycle',
        language: 'language',
        trafficofficerdistrict: 'trafficOfficerDistrict',
        traffic_officer_district: 'trafficOfficerDistrict',
        unavailabilitytype: 'unavailabilityType',
        unavailability_type: 'unavailabilityType',
        problemreason: 'problemReason',
        problem_reason: 'problemReason',
        problemaction: 'problemAction',
        problem_action: 'problemAction',
        jobofferstatus: 'jobOfferStatus',
        job_offer_status: 'jobOfferStatus',
        shipmentrequeststatus: 'shipmentRequestStatus',
        shipment_request_status: 'shipmentRequestStatus',
        shipmentstatus: 'shipmentStatus',
        shipment_status: 'shipmentStatus',
        jobstatus: 'jobStatus',
        job_status: 'jobStatus',
        kycverificationstatus: 'kycVerificationStatus',
        kyc_verification_status: 'kycVerificationStatus',
        kycstatus: 'kycVerificationStatus',
        userstatus: 'userStatus',
        user_status: 'userStatus',
        disputetype: 'disputeType',
        dispute_type: 'disputeType',
      }
      return enumFieldMap[lowerKey] || null
    }
    
    // Handle enum fields
    const enumName = getEnumNameFromKey(key)
    if (enumName && value && value !== 'null') {
      const enumValue = String(value)
      if (enumValue) {
        return getLocalizedLabel(enumName, enumValue)
      }
    }
    
    // Handle rating - display as stars (1-5)
    if (key.toLowerCase() === 'rating' && value && value !== 'null') {
      const ratingValue = Number(value)
      if (!isNaN(ratingValue) && ratingValue >= 0 && ratingValue <= 5) {
        return <Rating value={ratingValue} readOnly size="small" max={5} precision={0.5} />
      }
      return String(value)
    }
    
    // Handle date fields - display as date-time
    // Check if key suggests it's a date OR if value looks like an ISO date string
    const isDateKey = key.toLowerCase().includes('date') || key.toLowerCase().includes('at') || key.toLowerCase().includes('time')
    const isDateValue = typeof value === 'string' && (
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value) || // ISO format like 2025-12-17T12:23:33
      /^\d{4}-\d{2}-\d{2}/.test(value) // Date format like 2025-12-17
    )
    
    if (isDateKey || isDateValue) {
      try {
        const date = new Date(value)
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
        }
      } catch {
        // Fall through to string conversion
      }
    }
    
    // Handle roles array - display as chips with localized labels
    // This handles both "roles" and "user.roles" (from flattened driver objects)
    if ((key.toLowerCase() === 'roles' || key.toLowerCase().endsWith('.roles')) && Array.isArray(value)) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.map((role: string, index: number) => (
            <Chip
              key={index}
              label={getLocalizedLabel('userRole', role)}
              size="small"
              sx={{ mr: 0.5 }}
            />
          ))}
        </Box>
      )
    }
    
    // Handle nested user objects (createdBy, updatedBy, etc.) - display the "name" property
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Check if it's a user object with a "name" property
      if (value.name && typeof value.name === 'string') {
        return value.name
      }
      // Otherwise, stringify the object
      return JSON.stringify(value)
    }
    
    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No'
    }
    
    // Final check for null string
    const strValue = String(value)
    return strValue === 'null' ? '-' : strValue
  }

  // Check if account is Freighter - API uses 'type' field
  const isFreighter = selectedAccount
    ? String(selectedAccount.type || '').toUpperCase() === 'FREIGHTER'
    : false

  const isShipper = selectedAccount
    ? String(selectedAccount.type || '').toUpperCase() === 'SHIPPER'
    : false

  const { data: preferredVehiclesData, isLoading: preferredVehiclesLoading } = useQuery({
    queryKey: ['preferred-vehicles', selectedAccountId],
    queryFn: () => shipmentService.getShipperPreferredVehicles(selectedAccountId as string),
    enabled: !!selectedAccountId && isShipper && rightTab === 'preferred-vehicles',
  })

  const { data: blockedShippersData, isLoading: blockedShippersLoading } = useQuery({
    queryKey: ['blocked-shippers', selectedAccountId],
    queryFn: () => shipmentService.getFreighterBlockedShippers(selectedAccountId as string),
    enabled: !!selectedAccountId && isFreighter && rightTab === 'blocked-shippers',
  })

  const { data: privateFleetData, isLoading: privateFleetLoading } = useQuery({
    queryKey: ['private-fleet', selectedAccountId],
    queryFn: () => shipmentService.getFreighterPrivateFleet(selectedAccountId as string),
    enabled: !!selectedAccountId && isFreighter && rightTab === 'private-fleet',
  })

  /** Shipper's Preferred Freighters with names and their Private Fleet mark. */
  const preferredFreighterRows = useMemo(() => {
    const ids = preferredVehiclesData?.preferredFreighterAccountIds ?? []
    const privateIds = new Set(preferredVehiclesData?.privateFleetFreighterAccountIds ?? [])
    const accounts = getAccountsArray()
    const nameById = new Map<string, string | undefined>(
      accounts.map((a: { id: string; businessName?: string }) => [a.id, a.businessName])
    )
    return ids.map((id) => ({ id, businessName: nameById.get(id) || id, privateFleet: privateIds.has(id) }))
  }, [preferredVehiclesData, getAccountsArray])

  const preferredPlateNumbers = useMemo(
    () => preferredVehiclesData?.plateNumbers ?? [],
    [preferredVehiclesData]
  )

  const { data: preferredVehicleRows, isLoading: preferredVehicleDetailsLoading } = useQuery({
    queryKey: ['preferred-vehicle-details', selectedAccountId, preferredPlateNumbers, accountsData],
    queryFn: async () => {
      const accounts = getAccountsArray()
      const accountMap = new Map<string, { businessName?: string }>(
        accounts.map((a: { id: string; businessName?: string }) => [a.id, a])
      )
      const rows = await Promise.all(
        preferredPlateNumbers.map(async (plateNo) => {
          try {
            const vehicle = await vehicleService.getByPlate(plateNo)
            const freighterAccountId = vehicle?.accountId
            const freighterAccount = freighterAccountId ? accountMap.get(freighterAccountId) : undefined
            return {
              plateNo: vehicle?.plateNo || plateNo,
              freighterBusinessName: freighterAccount?.businessName || freighterAccountId || '—',
              vehicleId: vehicle?.id,
            }
          } catch {
            return {
              plateNo,
              freighterBusinessName: '—',
              vehicleId: undefined,
            }
          }
        })
      )
      return rows
    },
    enabled:
      !!selectedAccountId &&
      isShipper &&
      rightTab === 'preferred-vehicles' &&
      preferredPlateNumbers.length > 0,
  })

  const handleRefresh = () => {
    void refetchAccounts()
    void queryClient.invalidateQueries({ queryKey: ['kyc-pending'] })
    if (selectedAccountId) {
      void queryClient.invalidateQueries({ queryKey: ['account', selectedAccountId] })
      void queryClient.invalidateQueries({ queryKey: ['users', selectedAccountId] })
      void queryClient.invalidateQueries({ queryKey: ['drivers', selectedAccountId] })
      void queryClient.invalidateQueries({ queryKey: ['vehicles', selectedAccountId] })
      void queryClient.invalidateQueries({ queryKey: ['subscriptions-for-accounts'] })
      void queryClient.invalidateQueries({ queryKey: ['account-payments', selectedAccountId] })
      void queryClient.invalidateQueries({ queryKey: ['account-payouts', selectedAccountId] })
      void queryClient.invalidateQueries({ queryKey: ['preferred-vehicles', selectedAccountId] })
      void queryClient.invalidateQueries({ queryKey: ['private-fleet', selectedAccountId] })
      void queryClient.invalidateQueries({ queryKey: ['blocked-shippers', selectedAccountId] })
    }
  }

  // ==================== LOADING STATE ====================
  if (accountsLoading) {
    return (
      <Box sx={{ p: 2, flex: 1, minHeight: 0 }}>
        <TableSkeleton rows={10} columns={6} />
      </Box>
    )
  }

  if (accountsError) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ErrorCard
          title="Failed to load accounts"
          message={(accountsErrObj as Error)?.message}
          onRetry={() => refetchAccounts()}
        />
      </Box>
    )
  }

  // ==================== MAIN RENDER ====================
  return (
    <Box className="animate-fade-in" sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Top Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Search */}
            <TextField
              placeholder="Search accounts..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />

            {/* Account Type Filter */}
            <ToggleButtonGroup
              value={accountTypeFilter}
              exclusive
              onChange={(_, val) => val && setAccountTypeFilter(val)}
              size="small"
            >
              <ToggleButton value="ALL">All</ToggleButton>
              <ToggleButton value="SHIPPER">
                <ShipperIcon sx={{ mr: 0.5, fontSize: 18, color: shipperColor }} /> {getLocalizedLabel('accountType', 'SHIPPER')}s
              </ToggleButton>
              <ToggleButton value="FREIGHTER">
                <FreighterIcon sx={{ mr: 0.5, fontSize: 18, color: freighterColor }} /> {getLocalizedLabel('accountType', 'FREIGHTER')}s
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Status Filter */}
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(_, val) => val && setStatusFilter(val)}
              size="small"
            >
              <ToggleButton value="ALL">All</ToggleButton>
              <ToggleButton value="ACTIVE">{getLocalizedLabel('userStatus', 'ACTIVE')}</ToggleButton>
              <ToggleButton value="INACTIVE">{getLocalizedLabel('userStatus', 'DEACTIVATED')}</ToggleButton>
            </ToggleButtonGroup>

            {/* Pending KYC Filter */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={pendingKycFilter}
                  onChange={(e) => setPendingKycFilter(e.target.checked)}
                  sx={{
                    color: theme.palette.warning.main,
                    '&.Mui-checked': { color: theme.palette.warning.main },
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  Pending KYC
                </Typography>
              }
            />

            {/* Created-within Filter */}
            <TextField
              select
              size="small"
              label="Created"
              value={createdWithinDays}
              onChange={(e) => setCreatedWithinDays(Number(e.target.value))}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value={0}>Any time</MenuItem>
              <MenuItem value={1}>Last 24 hours</MenuItem>
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </TextField>

            {/* Account count */}
            <PageRefreshButton onRefresh={handleRefresh} loading={accountsFetching} />
            <Chip
              label={`${filteredAccounts.length} accounts`}
              size="small"
              sx={{ ml: 'auto' }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Main Content - Split View */}
      <Box
        id="unified-accounts-container"
        sx={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}
      >
        {/* Left Panel - Account List */}
        <Paper
          sx={{
            width: `${leftPanelWidth}%`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Accounts
            </Typography>
          </Box>
          <TableContainer 
            sx={{ 
              flex: 1, 
              overflow: 'auto', // Allow scrolling
              maxHeight: '100%',
              height: 0, // Force height calculation for sticky to work
              position: 'relative',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: 'background.paper',
                  '& th': {
                    backgroundColor: 'background.paper',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                  },
                }}
              >
                <TableRow>
                  <ResizableTableCell width={50}>Type</ResizableTableCell>
                  <ResizableTableCell>Status</ResizableTableCell>
                  <ResizableTableCell>Business Name</ResizableTableCell>
                  <ResizableTableCell>Business Address</ResizableTableCell>
                  <ResizableTableCell>Contact Email</ResizableTableCell>
                  <ResizableTableCell>Contact Phone</ResizableTableCell>
                  <ResizableTableCell>Rating</ResizableTableCell>
                  <ResizableTableCell width={80}>Users</ResizableTableCell>
                  <ResizableTableCell width={80}>Age</ResizableTableCell>
                  <ResizableTableCell width={60} sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAccounts.map((account: any) => {
                  // Get account type - API uses 'type' field
                  const accountType = String(account.type || '').toUpperCase()
                  const isSelected = selectedAccountId === account.id
                  const userCount = getUserCount(account.id)

                  return (
                    <TableRow
                      key={account.id}
                      hover
                      selected={isSelected}
                      onClick={() => handleAccountSelect(account.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{renderAccountIcon(accountType)}</TableCell>
                      <TableCell>
                        {renderCellValue('status', account.status)}
                      </TableCell>
                      <TableCell>{account.businessName || '-'}</TableCell>
                      <TableCell>{account.businessAddress || '-'}</TableCell>
                      <TableCell>{account.contactEmail || '-'}</TableCell>
                      <TableCell>{account.contactPhone || '-'}</TableCell>
                      <TableCell>{renderCellValue('rating', account.rating)}</TableCell>
                      <TableCell>
                        <Badge
                          badgeContent={userCount}
                          color="default"
                          sx={{
                            '& .MuiBadge-badge': {
                              bgcolor: 'grey.500',
                              color: 'white',
                            },
                          }}
                        >
                          <PeopleIcon color="action" />
                        </Badge>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={account.createdAt ? `Created ${fmtDateTime(account.createdAt)}` : ''}>
                          <span>{fmtAge(account.createdAt)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()} sx={stickyActionsBodySx}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            setActionMenuAnchor(e.currentTarget)
                            setActionMenuAccountId(account.id)
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Resizable Splitter */}
        <Box
          onMouseDown={(e) => {
            // Only handle if the event didn't come from a table resize handle
            const target = e.target as HTMLElement
            if (target.closest('[data-resize-handle]')) {
              return // Ignore if it's from a resize handle
            }
            e.preventDefault()
            e.stopPropagation()
            setIsResizing(true)
          }}
          sx={{
            width: 6,
            cursor: 'col-resize',
            bgcolor: isResizing ? 'primary.main' : 'grey.300',
            '&:hover': { bgcolor: 'primary.light' },
            transition: 'background-color 0.2s',
            userSelect: 'none',
            flexShrink: 0,
            position: 'relative',
            zIndex: 5, // Higher than table resize handles
          }}
        />

        {/* Right Panel - Tabs */}
        <Paper
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0, // Prevent flex item from overflowing
          }}
        >
          {selectedAccountId ? (
            <>
              <Tabs
                value={rightTab}
                onChange={(_, v) => setRightTab(v)}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  minHeight: 48,
                  flexShrink: 0, // Prevent tabs from shrinking
                }}
              >
                <Tab label="Overview" value="overview" />
                <Tab label="Account Properties" value="properties" />
                {isFreighter && <Tab label="Vehicles" value="vehicles" />}
                {isFreighter && <Tab label="Drivers" value="drivers" />}
                {isShipper && <Tab label="Preferred Vehicles" value="preferred-vehicles" />}
                {isFreighter && <Tab label="Private Fleet" value="private-fleet" />}
                {isFreighter && <Tab label="Blocked Shippers" value="blocked-shippers" />}
                <Tab label="Users" value="users" />
                <Tab label="Finance" value="finance" />
              </Tabs>

              {/* Tab Panels Container */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Overview Tab */}
                <TabPanel value={rightTab} tabKey="overview">
                  <AccountOverview
                    account={selectedAccount}
                    isFreighter={isFreighter}
                    userCount={getUserCount(selectedAccountId)}
                    score={getAccountScore(selectedAccountId)}
                    vehiclesCount={(Array.isArray(vehiclesData) ? vehiclesData : []).length}
                    driversCount={(Array.isArray(driversData) ? driversData : []).length}
                    kycPendingCount={
                      (kycPendingData?.content || kycPendingData || []).filter(
                        (k: any) => k.accountId === selectedAccountId,
                      ).length
                    }
                    subscriptions={selectedAccountSubscriptions}
                    planById={planById}
                  />
                </TabPanel>

              {/* Account Properties Tab */}
              <TabPanel value={rightTab} tabKey="properties">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Account Properties</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editMode}
                          onChange={(e) => setEditMode(e.target.checked)}
                        />
                      }
                      label="Edit Mode"
                    />
                    {editMode && (
                      <Button
                        variant="contained"
                        onClick={handleSaveAccount}
                        disabled={updateAccountMutation.isPending}
                      >
                        Save Changes
                      </Button>
                    )}
                  </Box>
                </Box>
                {editedAccount && (
                  <EntityProperties
                    data={editedAccount as Record<string, unknown>}
                    groups={accountSchema}
                    editMode={editMode}
                    onChange={(next) => setEditedAccount(next as typeof editedAccount)}
                  />
                )}
              </TabPanel>

              {/* Tab 2: Vehicles (Freighter only) */}
              {isFreighter && (
                <TabPanel value={rightTab} tabKey="vehicles">
                  <Typography variant="h6" gutterBottom>Vehicles</Typography>
                  <TableContainer 
                    sx={{ 
                      maxHeight: 'calc(100vh - 300px)',
                      overflow: 'auto',
                      height: '100%',
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          backgroundColor: 'background.paper',
                          '& th': {
                            backgroundColor: 'background.paper',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                          },
                        }}
                      >
                        <TableRow>
                          <ResizableTableCell>Plate Number</ResizableTableCell>
                          <ResizableTableCell>Maker</ResizableTableCell>
                          <ResizableTableCell>Model</ResizableTableCell>
                          <ResizableTableCell>Year</ResizableTableCell>
                          <ResizableTableCell>Type</ResizableTableCell>
                          <ResizableTableCell>Length</ResizableTableCell>
                          <ResizableTableCell>Width</ResizableTableCell>
                          <ResizableTableCell>Height</ResizableTableCell>
                          <ResizableTableCell>Features</ResizableTableCell>
                          <ResizableTableCell>Loading Side</ResizableTableCell>
                          <ResizableTableCell>KYC Verification</ResizableTableCell>
                          <ResizableTableCell width={60} sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(Array.isArray(vehiclesData) ? vehiclesData : []).map((vehicle: any) => (
                          <TableRow
                            key={vehicle.id}
                            hover
                            onDoubleClick={() => openEntityDialog('vehicle', vehicle, false)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{vehicle.licensePlate || vehicle.plateNo || vehicle.plateNumber || '-'}</TableCell>
                            <TableCell>{vehicle.maker || vehicle.make || '-'}</TableCell>
                            <TableCell>{vehicle.model || '-'}</TableCell>
                            <TableCell>{vehicle.year || '-'}</TableCell>
                            <TableCell>{renderCellValue('vehicleType', vehicle.type || vehicle.vehicleType)}</TableCell>
                            <TableCell>{vehicle.lengthM ? `${vehicle.lengthM}m` : '-'}</TableCell>
                            <TableCell>{vehicle.widthM ? `${vehicle.widthM}m` : '-'}</TableCell>
                            <TableCell>{vehicle.heightM ? `${vehicle.heightM}m` : '-'}</TableCell>
                            <TableCell>
                              {[
                                vehicle.boxTruck || vehicle.box ? 'Box' : null,
                                vehicle.curtainsider ? 'Curtainsider' : null,
                                vehicle.flatbed ? 'Flatbed' : null,
                                vehicle.hasRefrigeration || vehicle.refreg ? 'Refrigerated' : null,
                              ]
                                .filter(Boolean)
                                .join(', ') || '-'}
                            </TableCell>
                            <TableCell>{renderCellValue('loadingSide', vehicle.loadingSide)}</TableCell>
                            <TableCell>{renderKycChip(vehicle.kycVerificationStatus)}</TableCell>
                            <TableCell sx={stickyActionsBodySx}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setVehicleMenuAnchor(e.currentTarget)
                                  setSelectedVehicle(vehicle)
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>
              )}

              {/* Tab 3: Drivers (Freighter only) */}
              {isFreighter && (
                <TabPanel value={rightTab} tabKey="drivers">
                  <Typography variant="h6" gutterBottom>Drivers</Typography>
                  <TableContainer 
                    sx={{ 
                      maxHeight: 'calc(100vh - 300px)',
                      overflow: 'auto',
                      height: '100%',
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          backgroundColor: 'background.paper',
                          '& th': {
                            backgroundColor: 'background.paper',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                          },
                        }}
                      >
                        <TableRow>
                          <ResizableTableCell>User Name</ResizableTableCell>
                          <ResizableTableCell>User Roles</ResizableTableCell>
                          <ResizableTableCell>User Email</ResizableTableCell>
                          <ResizableTableCell>Phone</ResizableTableCell>
                          <ResizableTableCell>Status</ResizableTableCell>
                          <ResizableTableCell>KYC Verification</ResizableTableCell>
                          <ResizableTableCell width={60} sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(Array.isArray(driversData) ? driversData : []).map((driver: any) => {
                          const flattenedDriver = flattenDriver(driver)
                          return (
                            <TableRow
                              key={driver.id}
                              hover
                              onDoubleClick={() => openEntityDialog('driver', driver, false)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>{flattenedDriver['user.name'] || driver.user?.name || '-'}</TableCell>
                              <TableCell>
                                {flattenedDriver['user.roles'] && Array.isArray(flattenedDriver['user.roles']) ? (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {flattenedDriver['user.roles'].map((role: string) => (
                                      <Chip key={role} label={getLocalizedLabel('userRole', role)} size="small" />
                                    ))}
                                  </Box>
                                ) : driver.user?.roles && Array.isArray(driver.user.roles) ? (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {driver.user.roles.map((role: string) => (
                                      <Chip key={role} label={getLocalizedLabel('userRole', role)} size="small" />
                                    ))}
                                  </Box>
                                ) : '-'}
                              </TableCell>
                              <TableCell>{flattenedDriver['user.email'] || driver.user?.email || '-'}</TableCell>
                              <TableCell>{flattenedDriver['user.phone'] || flattenedDriver['user.phoneNumber'] || driver.user?.phone || driver.user?.phoneNumber || '-'}</TableCell>
                              <TableCell>{renderCellValue('status', flattenedDriver.status || driver.status)}</TableCell>
                              <TableCell>{renderKycChip(driver.kycVerificationStatus)}</TableCell>
                            <TableCell sx={stickyActionsBodySx}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setDriverMenuAnchor(e.currentTarget)
                                  setSelectedDriver(driver)
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>
              )}

              {/* Preferred Vehicles (Shipper only) */}
              {isShipper && (
                <TabPanel value={rightTab} tabKey="preferred-vehicles">
                  <Typography variant="h6" gutterBottom>
                    Preferred Vehicles
                  </Typography>
                  {preferredVehiclesLoading || preferredVehicleDetailsLoading ? (
                    <TableSkeleton rows={4} columns={2} />
                  ) : preferredPlateNumbers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No preferred vehicles configured for this shipper.
                    </Typography>
                  ) : (
                    <TableContainer
                      sx={{
                        maxHeight: 'calc(100vh - 300px)',
                        overflow: 'auto',
                        height: '100%',
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead
                          sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            backgroundColor: 'background.paper',
                            '& th': {
                              backgroundColor: 'background.paper',
                              position: 'sticky',
                              top: 0,
                              zIndex: 10,
                            },
                          }}
                        >
                          <TableRow>
                            <ResizableTableCell>Freighter Business Name</ResizableTableCell>
                            <ResizableTableCell>Plate Number</ResizableTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(preferredVehicleRows ?? []).map((row, index) => (
                            <TableRow key={row.vehicleId || `${row.plateNo}-${index}`} hover>
                              <TableCell>{row.freighterBusinessName}</TableCell>
                              <TableCell>{row.plateNo}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Preferred Freighters
                  </Typography>
                  {preferredVehiclesLoading ? (
                    <TableSkeleton rows={3} columns={2} />
                  ) : preferredFreighterRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No preferred freighters configured for this shipper.
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <ResizableTableCell>Freighter Business Name</ResizableTableCell>
                            <ResizableTableCell>Private Fleet</ResizableTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {preferredFreighterRows.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell>{row.businessName}</TableCell>
                              <TableCell>
                                {row.privateFleet ? (
                                  <Chip size="small" color="primary" label="Private Fleet" />
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </TabPanel>
              )}

              {/* Private Fleet (Freighter only, read-only) */}
              {isFreighter && (
                <TabPanel value={rightTab} tabKey="private-fleet">
                  <Typography variant="h6" gutterBottom>
                    Private Fleet
                  </Typography>
                  {privateFleetLoading ? (
                    <TableSkeleton rows={4} columns={3} />
                  ) : !privateFleetData ? (
                    <Typography variant="body2" color="text.secondary">
                      Private Fleet setting is not available.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Restriction: </strong>
                        {privateFleetData.wholeFleet
                          ? 'Whole fleet — offered only to allowed shippers'
                          : privateFleetData.vehicleIds.length > 0
                            ? `${privateFleetData.vehicleIds.length} vehicle(s) — offered only to allowed shippers`
                            : 'None — open to all shippers'}
                      </Typography>
                      {!privateFleetData.wholeFleet && privateFleetData.vehicleIds.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          {privateFleetData.vehicleIds.map((vehicleId) => {
                            const vehicle = (Array.isArray(vehiclesData) ? vehiclesData : []).find(
                              (v: any) => v.id === vehicleId
                            ) as any
                            return (
                              <Chip
                                key={vehicleId}
                                size="small"
                                label={vehicle?.licensePlate || vehicle?.plateNo || vehicle?.plateNumber || vehicleId}
                              />
                            )
                          })}
                        </Box>
                      )}
                      {privateFleetData.shippers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No shipper has marked this freighter as its Private Fleet.
                        </Typography>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <ResizableTableCell>Shipper Business Name</ResizableTableCell>
                                <ResizableTableCell>Marks as Private Fleet</ResizableTableCell>
                                <ResizableTableCell>Allowed on Restricted Vehicles</ResizableTableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {privateFleetData.shippers.map((row) => (
                                <TableRow key={row.shipperAccountId} hover>
                                  <TableCell>{row.businessName || row.shipperAccountId}</TableCell>
                                  <TableCell>{row.markedPrivateFleet ? 'Yes' : 'No'}</TableCell>
                                  <TableCell>
                                    {row.allowed ? <Chip size="small" color="primary" label="Allowed" /> : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </>
                  )}
                </TabPanel>
              )}

              {/* Blocked Shippers (Freighter only, read-only) */}
              {isFreighter && (
                <TabPanel value={rightTab} tabKey="blocked-shippers">
                  <Typography variant="h6" gutterBottom>
                    Blocked Shippers
                  </Typography>
                  {blockedShippersLoading ? (
                    <TableSkeleton rows={4} columns={2} />
                  ) : (blockedShippersData ?? []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      This freighter has not blocked any shipper.
                    </Typography>
                  ) : (
                    <TableContainer
                      sx={{
                        maxHeight: 'calc(100vh - 300px)',
                        overflow: 'auto',
                        height: '100%',
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead
                          sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            backgroundColor: 'background.paper',
                            '& th': {
                              backgroundColor: 'background.paper',
                              position: 'sticky',
                              top: 0,
                              zIndex: 10,
                            },
                          }}
                        >
                          <TableRow>
                            <ResizableTableCell>Shipper Business Name</ResizableTableCell>
                            <ResizableTableCell>Blocked At</ResizableTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(blockedShippersData ?? []).map((row) => (
                            <TableRow key={row.shipperAccountId} hover>
                              <TableCell>{row.shipperBusinessName || row.shipperAccountId}</TableCell>
                              <TableCell>
                                {row.blockedAt ? new Date(row.blockedAt).toLocaleString() : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </TabPanel>
              )}

              {/* Tab 4 (or 2 for Shippers): Users */}
              <TabPanel value={rightTab} tabKey="users">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Users</Typography>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setInviteUserDialogOpen(true)}
                  >
                    Invite User
                  </Button>
                </Box>
                <TableContainer 
                  sx={{ 
                    maxHeight: 'calc(100vh - 300px)',
                    overflow: 'auto',
                    height: '100%',
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: 'background.paper',
                        '& th': {
                          backgroundColor: 'background.paper',
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                        },
                      }}
                    >
                      <TableRow>
                        <ResizableTableCell>Name</ResizableTableCell>
                        <ResizableTableCell>Status</ResizableTableCell>
                        <ResizableTableCell>Roles</ResizableTableCell>
                        <ResizableTableCell>Email</ResizableTableCell>
                        <ResizableTableCell width={60} sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(usersData?.content || usersData || []).map((user: any) => (
                        <TableRow
                          key={user.id}
                          hover
                          onDoubleClick={() => openUserDetail(user.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{user.name || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={getLocalizedLabel('userStatus', user.status) || user.status || '—'}
                              size="small"
                              color={
                                user.status === 'ACTIVE'
                                  ? 'success'
                                  : user.status === 'PENDING_APPROVAL'
                                  ? 'warning'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {user.roles && Array.isArray(user.roles) ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {user.roles.map((role: string) => (
                                  <Chip key={role} label={getLocalizedLabel('userRole', role)} size="small" />
                                ))}
                              </Box>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{user.email || '-'}</TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()} sx={stickyActionsBodySx}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                setUserMenuAnchor(e.currentTarget)
                                setSelectedUser(user)
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              {/* Finance Tab */}
              <TabPanel value={rightTab} tabKey="finance">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Recent Payments
                        </Typography>
                        {(accountPaymentsPage?.content || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No payments.
                          </Typography>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Date</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(accountPaymentsPage?.content || [])
                                .slice(0, 10)
                                .map((p: any) => (
                                  <TableRow key={p.id} hover>
                                    <TableCell>
                                      <Chip
                                        label={getLocalizedLabel('paymentStatus', p.status) || p.status}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {formatCurrency(p.grossAmount ?? p.amount ?? 0)}{' '}
                                      {p.currency || ''}
                                    </TableCell>
                                    <TableCell>
                                      {p.createdAt
                                        ? new Date(p.createdAt).toLocaleDateString()
                                        : '—'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Recent Payouts
                        </Typography>
                        {(accountPayoutsPage?.content || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No payouts.
                          </Typography>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Date</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(accountPayoutsPage?.content || [])
                                .slice(0, 10)
                                .map((p: any) => (
                                  <TableRow key={p.id} hover>
                                    <TableCell>
                                      <Chip
                                        label={getLocalizedLabel('paymentStatus', p.status) || p.status}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {formatCurrency(p.amount ?? 0)} {p.currency || ''}
                                    </TableCell>
                                    <TableCell>
                                      {(p.dueDate || p.payoutDate || p.scheduledDate || p.createdAt)
                                        ? new Date(
                                            p.dueDate || p.payoutDate || p.scheduledDate || p.createdAt
                                          ).toLocaleDateString()
                                        : '—'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <Typography>Select an account to view details</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ==================== MENUS ==================== */}
      {/* Account Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => {
          setActionMenuAnchor(null)
          setSubscriptionMenuAnchor(null)
        }}
      >
        <MenuItem
          onClick={() => {
            const account = filteredAccounts.find((a: any) => a.id === actionMenuAccountId)
            if (account?.status === 'ACTIVE') {
              deactivateMutation.mutate(actionMenuAccountId!)
            } else {
              activateMutation.mutate(actionMenuAccountId!)
            }
            setActionMenuAnchor(null)
            setSubscriptionMenuAnchor(null)
          }}
        >
          <PowerIcon sx={{ mr: 1 }} fontSize="small" />
          Activate/Deactivate
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedAccountId(actionMenuAccountId)
            setInviteUserDialogOpen(true)
            setActionMenuAnchor(null)
            setSubscriptionMenuAnchor(null)
          }}
        >
          <PersonAddIcon sx={{ mr: 1 }} fontSize="small" />
          Invite User
        </MenuItem>
        <AccountSubscriptionMenuEntry
          onOpenSubmenu={(el) => setSubscriptionMenuAnchor(el)}
        />
      </Menu>

      <AccountSubscriptionActions
        accountId={actionMenuAccountId}
        accountType={actionMenuAccountType}
        subscriptions={actionMenuSubscriptions}
        plans={subscriptionPlans}
        submenuAnchor={subscriptionMenuAnchor}
        parentMenuOpen={Boolean(actionMenuAnchor)}
        onSubmenuClose={() => setSubscriptionMenuAnchor(null)}
        onCloseAll={() => {
          setActionMenuAnchor(null)
          setSubscriptionMenuAnchor(null)
        }}
      />

      {/* Vehicle Actions Menu */}
      <Menu
        anchorEl={vehicleMenuAnchor}
        open={Boolean(vehicleMenuAnchor)}
        onClose={() => setVehicleMenuAnchor(null)}
      >
        <MenuItem onClick={() => { openEntityDialog('vehicle', selectedVehicle, false); setVehicleMenuAnchor(null) }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" /> View/Edit Properties
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedVehicle?.id) {
              if (selectedVehicle.isActive !== false) {
                deactivateVehicleMutation.mutate(selectedVehicle.id)
              } else {
                activateVehicleMutation.mutate(selectedVehicle.id)
              }
            }
            setVehicleMenuAnchor(null)
          }}
        >
          <PowerIcon sx={{ mr: 1 }} fontSize="small" />
          {selectedVehicle?.isActive !== false ? 'Deactivate' : 'Activate'}
        </MenuItem>
        <MenuItem onClick={() => { if (selectedVehicle?.id) setPendingDelete({ kind: 'vehicle', id: selectedVehicle.id, label: selectedVehicle.plateNo || selectedVehicle.plateNumber || 'this vehicle' }); setVehicleMenuAnchor(null) }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" /> Delete Vehicle
        </MenuItem>
        <MenuItem onClick={() => { openCalendarDialog('vehicle', selectedVehicle?.id, selectedVehicle?.plateNumber || 'Vehicle'); setVehicleMenuAnchor(null) }}>
          <CalendarIcon sx={{ mr: 1 }} fontSize="small" /> Availability Calendar
        </MenuItem>
        <MenuItem onClick={() => { openKycDialog('vehicle', selectedVehicle?.id, selectedVehicle?.plateNumber || 'Vehicle', 'initiate', selectedVehicle?.accountId); setVehicleMenuAnchor(null) }}>
          <VerifiedUserIcon sx={{ mr: 1 }} fontSize="small" /> Initiate KYC
        </MenuItem>
        <MenuItem onClick={() => { openKycDialog('vehicle', selectedVehicle?.id, selectedVehicle?.plateNumber || 'Vehicle', 'review', selectedVehicle?.accountId); setVehicleMenuAnchor(null) }}>
          <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" /> Review KYC
        </MenuItem>
        <MenuItem onClick={() => { openDocumentDialog('vehicle', selectedVehicle?.id, selectedVehicle?.plateNumber || 'Vehicle', selectedVehicle); setVehicleMenuAnchor(null) }}>
          <DescriptionIcon sx={{ mr: 1 }} fontSize="small" /> Analyze Documents
        </MenuItem>
        <MenuItem onClick={() => { openScoreDialog('vehicle', selectedVehicle?.id, selectedVehicle?.plateNumber || 'Vehicle'); setVehicleMenuAnchor(null) }}>
          <TrendingUpIcon sx={{ mr: 1 }} fontSize="small" /> Adjust Score
        </MenuItem>
        <MenuItem onClick={() => { openScoreDialog('vehicle', selectedVehicle?.id, selectedVehicle?.plateNumber || 'Vehicle'); setVehicleMenuAnchor(null) }}>
          <RefreshIcon sx={{ mr: 1 }} fontSize="small" /> Reset Score
        </MenuItem>
      </Menu>

      {/* Driver Actions Menu */}
      <Menu
        anchorEl={driverMenuAnchor}
        open={Boolean(driverMenuAnchor)}
        onClose={() => setDriverMenuAnchor(null)}
      >
        <MenuItem onClick={() => { openEntityDialog('driver', selectedDriver, false); setDriverMenuAnchor(null) }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" /> View/Edit Properties
        </MenuItem>
        <MenuItem onClick={() => { if (selectedDriver?.id) setPendingDelete({ kind: 'driver', id: selectedDriver.id, label: selectedDriver.user?.name || selectedDriver.licenseNo || 'this driver' }); setDriverMenuAnchor(null) }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" /> Delete Driver
        </MenuItem>
        <MenuItem onClick={() => { openCalendarDialog('driver', selectedDriver?.id, selectedDriver?.name || 'Driver'); setDriverMenuAnchor(null) }}>
          <CalendarIcon sx={{ mr: 1 }} fontSize="small" /> Availability Calendar
        </MenuItem>
        <MenuItem onClick={() => { openKycDialog('driver', selectedDriver?.id, selectedDriver?.name || 'Driver', 'initiate', selectedDriver?.accountId); setDriverMenuAnchor(null) }}>
          <VerifiedUserIcon sx={{ mr: 1 }} fontSize="small" /> Initiate KYC
        </MenuItem>
        <MenuItem onClick={() => { openKycDialog('driver', selectedDriver?.id, selectedDriver?.name || 'Driver', 'review', selectedDriver?.accountId); setDriverMenuAnchor(null) }}>
          <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" /> Review KYC
        </MenuItem>
        <MenuItem onClick={() => { openDocumentDialog('driver', selectedDriver?.id, selectedDriver?.name || 'Driver', selectedDriver); setDriverMenuAnchor(null) }}>
          <DescriptionIcon sx={{ mr: 1 }} fontSize="small" /> Analyze Documents
        </MenuItem>
        <MenuItem onClick={() => { openScoreDialog('driver', selectedDriver?.id, selectedDriver?.name || 'Driver'); setDriverMenuAnchor(null) }}>
          <TrendingUpIcon sx={{ mr: 1 }} fontSize="small" /> Adjust Score
        </MenuItem>
        <MenuItem onClick={() => { openScoreDialog('driver', selectedDriver?.id, selectedDriver?.name || 'Driver'); setDriverMenuAnchor(null) }}>
          <RefreshIcon sx={{ mr: 1 }} fontSize="small" /> Reset Score
        </MenuItem>
      </Menu>

      {/* User Actions Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
      >
        <MenuItem onClick={() => { if (selectedUser?.id) openUserDetail(selectedUser.id); setUserMenuAnchor(null) }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" /> View / Edit Details
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedUser?.status === 'ACTIVE') {
            deactivateUserMutation.mutate(selectedUser.id)
          } else if (selectedUser?.id) {
            activateUserMutation.mutate(selectedUser.id)
          }
          setUserMenuAnchor(null)
        }}>
          <PowerIcon sx={{ mr: 1 }} fontSize="small" />
          {selectedUser?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
        </MenuItem>
        <MenuItem onClick={() => { if (selectedUser?.id) userService.resendInvitation(selectedUser.id); setUserMenuAnchor(null) }}>
          <SendIcon sx={{ mr: 1 }} fontSize="small" /> Resend Invitation
        </MenuItem>
        <MenuItem onClick={() => { if (selectedUser?.id) setPendingDelete({ kind: 'user', id: selectedUser.id, label: selectedUser.name || 'this user' }); setUserMenuAnchor(null) }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" /> Delete User
        </MenuItem>
        <MenuItem onClick={() => { openCalendarDialog('user', selectedUser?.id, selectedUser?.name || 'User'); setUserMenuAnchor(null) }}>
          <CalendarIcon sx={{ mr: 1 }} fontSize="small" /> Availability Calendar
        </MenuItem>
        <MenuItem onClick={() => { openScoreDialog('user', selectedUser?.id, selectedUser?.name || 'User'); setUserMenuAnchor(null) }}>
          <TrendingUpIcon sx={{ mr: 1 }} fontSize="small" /> Adjust Score
        </MenuItem>
        <MenuItem onClick={() => { openScoreDialog('user', selectedUser?.id, selectedUser?.name || 'User'); setUserMenuAnchor(null) }}>
          <RefreshIcon sx={{ mr: 1 }} fontSize="small" /> Reset Score
        </MenuItem>
      </Menu>

      {/* ==================== DIALOGS ==================== */}
      {/* Invite User Dialog (shared component) */}
      <InviteUserDialog
        open={inviteUserDialogOpen}
        onClose={() => setInviteUserDialogOpen(false)}
        targetAccountId={selectedAccountId || undefined}
        onInvited={() => {
          queryClient.invalidateQueries({ queryKey: ['users', selectedAccountId] })
        }}
      />

      {/* Shared delete confirmation (typed) for vehicle / driver / user */}
      <ConfirmDialog
        open={!!pendingDelete}
        title={pendingDelete ? `Delete ${pendingDelete.kind}?` : 'Delete?'}
        description={
          pendingDelete ? (
            <>
              You're about to delete <strong>{pendingDelete.label}</strong>. This cannot be
              undone.
            </>
          ) : (
            'This cannot be undone.'
          )
        }
        confirmPhrase="DELETE"
        confirmHelper="Type DELETE to confirm."
        confirmLabel="Delete"
        severity="error"
        loading={
          deleteVehicleMutation.isPending ||
          deleteDriverMutation.isPending ||
          deleteUserMutation.isPending
        }
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return
          if (pendingDelete.kind === 'vehicle') deleteVehicleMutation.mutate(pendingDelete.id)
          else if (pendingDelete.kind === 'driver') deleteDriverMutation.mutate(pendingDelete.id)
          else deleteUserMutation.mutate(pendingDelete.id)
          setPendingDelete(null)
        }}
      />

      {/* User Detail Dialog (shared component) */}
      <UserDetailDialog
        open={userDetailOpen}
        onClose={() => {
          setUserDetailOpen(false)
          setUserDetailId(undefined)
        }}
        userId={userDetailId}
        onActionComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['users', selectedAccountId] })
        }}
      />

      {/* Entity Properties Dialog */}
      <Dialog
        open={entityDialogOpen}
        onClose={() => {
          setEntityDialogOpen(false)
          setEntityDialogTab(0)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {entityDialogType.charAt(0).toUpperCase() + entityDialogType.slice(1)} Properties
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={entityDialogEditMode}
                  onChange={(e) => setEntityDialogEditMode(e.target.checked)}
                />
              }
              label="Edit Mode"
            />
          </Box>
        </DialogTitle>
        {(entityDialogType === 'vehicle' || entityDialogType === 'driver') &&
          entityDialogData?.id && (
            <Tabs
              value={entityDialogTab}
              onChange={(_, v) => setEntityDialogTab(v)}
              sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Properties" />
              <Tab label="Availability" />
            </Tabs>
          )}
        <DialogContent dividers>
          {entityDialogData?.id &&
            (entityDialogType === 'vehicle' || entityDialogType === 'driver') &&
            entityDialogTab === 0 && (
              <Box sx={{ mb: 3 }}>
                <SubjectDocuments
                  subjectType={entityDialogType === 'driver' ? 'DRIVER' : 'VEHICLE'}
                  subjectId={entityDialogData.id as string}
                />
              </Box>
            )}
          {entityDialogData && entityDialogTab === 0 && (
            <EntityProperties
              data={entityDialogData as Record<string, unknown>}
              groups={
                entityDialogType === 'vehicle'
                  ? vehicleSchema
                  : entityDialogType === 'driver'
                  ? driverSchema
                  : userSchema
              }
              editMode={entityDialogEditMode}
              onChange={(next) => setEntityDialogData(next)}
            />
          )}
          {entityDialogData && entityDialogType === 'vehicle' && entityDialogTab === 1 && (
            <VehicleUnavailabilityCalendar vehicleId={entityDialogData.id as string} />
          )}
          {entityDialogData && entityDialogType === 'driver' && entityDialogTab === 1 && (
            <VehicleUnavailabilityCalendar driverId={entityDialogData.id as string} />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEntityDialogOpen(false)
              setEntityDialogTab(0)
            }}
          >
            Close
          </Button>
          {entityDialogEditMode && entityDialogTab === 0 && (
            <Button
              variant="contained"
              disabled={entitySaveMutation.isPending}
              onClick={() => entitySaveMutation.mutate()}
            >
              {entitySaveMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Score Adjustment Dialog */}
      <Dialog open={scoreDialogOpen} onClose={() => setScoreDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust Score - {scoreDialogEntity?.name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Score Adjustment"
            type="number"
            value={scoreAdjustment}
            onChange={(e) => setScoreAdjustment(Number(e.target.value))}
            margin="normal"
            helperText="Positive or negative value"
          />
          <TextField
            fullWidth
            label="Reason"
            value={scoreReason}
            onChange={(e) => setScoreReason(e.target.value)}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (scoreDialogEntity) {
                adjustScoreMutation.mutate({
                  entityType: scoreDialogEntity.type,
                  entityId: scoreDialogEntity.id,
                  request: { adjustment: scoreAdjustment, reason: scoreReason },
                })
              }
            }}
            disabled={!scoreReason || adjustScoreMutation.isPending}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calendar Dialog */}
      <Dialog open={calendarDialogOpen} onClose={() => setCalendarDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Availability Calendar — {calendarEntity?.name}</DialogTitle>
        <DialogContent dividers>
          {calendarEntity?.type === 'vehicle' && calendarEntity.id ? (
            <VehicleUnavailabilityCalendar vehicleId={calendarEntity.id} />
          ) : (
            <Alert severity="info">
              Availability calendar for {calendarEntity?.type ?? 'this entity'} is not yet supported.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* KYC Verification Review Dialog (shared) */}
      <VerificationReviewDialog
        open={kycDialogOpen}
        onClose={() => {
          setKycDialogOpen(false)
          setKycReviewVerificationId(undefined)
          setKycInitiateParams(undefined)
        }}
        verificationId={kycReviewVerificationId}
        initiate={kycInitiateParams}
        onActionComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['kyc-pending'] })
        }}
      />

      {/* Document Analysis Dialog */}
      <Dialog open={documentDialogOpen} onClose={() => setDocumentDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Document Analysis - {documentEntity?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Uploaded Documents
              </Typography>
              {documentEntity?.id ? (
                <SubjectDocuments
                  subjectType={documentEntity.type === 'driver' ? 'DRIVER' : 'VEHICLE'}
                  subjectId={documentEntity.id}
                  title={null}
                />
              ) : (
                <Alert severity="info">No documents available</Alert>
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                User Provided Properties
              </Typography>
              {documentEntity?.data &&
                Object.entries(documentEntity.data).map(([key, value]) => {
                  // Handle null, undefined, empty string, and "null" string
                  const displayValue = (() => {
                    if (value === null || value === undefined || value === '' || value === 'null') {
                      return '-'
                    }
                    // Handle date fields - format as date-time
                    // Check if key suggests it's a date OR if value looks like an ISO date string
                    const isDateKey = key.toLowerCase().includes('date') || key.toLowerCase().includes('at') || key.toLowerCase().includes('time')
                    const isDateValue = typeof value === 'string' && (
                      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value) || // ISO format
                      /^\d{4}-\d{2}-\d{2}/.test(value) // Date format
                    )
                    
                    if (isDateKey || isDateValue) {
                      try {
                        const date = new Date(value as string)
                        if (!isNaN(date.getTime())) {
                          return date.toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          })
                        }
                      } catch {
                        // Fall through
                      }
                    }
                    // Handle nested user objects - show name if available
                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                      if ((value as any).name && typeof (value as any).name === 'string') {
                        return (value as any).name
                      }
                      return JSON.stringify(value)
                    }
                    return String(value)
                  })()

                  // Handle rating - display as stars
                  if (key.toLowerCase() === 'rating' && value && value !== 'null') {
                    const ratingValue = Number(value)
                    if (!isNaN(ratingValue) && ratingValue >= 0 && ratingValue <= 5) {
                      return (
                        <Box key={key} sx={{ mb: 1 }}>
                          <Typography variant="body2">
                            <strong>{formatAttributeName(key)}:</strong>
                          </Typography>
                          <Rating value={ratingValue} readOnly size="small" max={5} precision={0.5} />
                        </Box>
                      )
                    }
                  }

                  return (
                    <Typography key={key} variant="body2">
                      <strong>{formatAttributeName(key)}:</strong> {displayValue}
                    </Typography>
                  )
                })}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
